// Trabajadores de grupos de atención prioritaria (lote D, M49, Anexo 1 TH 1). DATOS SENSIBLES de salud: se guardan en
// una tabla aparte (worker_priority_conditions) que solo lee y escribe el administrador de la empresa (ni el manager, ni
// el viewer, ni el superadmin). Minimización: solo lo que pide TH 1, sin diagnósticos; la enfermedad catastrófica es
// solo "sí". En los informes solo sale "N trabajadores con condición de atención prioritaria considerada", nunca
// nombres ni tipo de condición. Ningún PDF lleva el detalle por persona.

import { supabase } from '../../supabaseClient';

export const EDAD_ADULTO_MAYOR = 65;

export interface FilaCondicion {
  id: string; company_id: string; worker_id: string | null; declared_none: boolean; older_adult: boolean; lactation: boolean;
  pregnancy: boolean; disability: boolean; disability_pct: number | null; catastrophic_illness: boolean;
  /** "Otra condición declarada": solo viene del traslado de la ficha anterior (M49); la app la quita, no la pone. */
  other_declared: boolean; origin: OrigenCondicion;
  registered_at: string; registered_by: string | null;
}
export type OrigenCondicion = 'app' | 'traslado_ficha_anterior';
export const ETIQUETA_OTRA_CONDICION = 'Otra condición declarada (trasladada desde la ficha anterior, sin detalle)';
export type Condicion = 'older_adult' | 'lactation' | 'pregnancy' | 'disability' | 'catastrophic_illness';
export const CONDICIONES: Condicion[] = ['older_adult', 'lactation', 'pregnancy', 'disability', 'catastrophic_illness'];
export const ETIQUETA_CONDICION: Record<Condicion, string> = {
  older_adult: 'Adulto mayor', lactation: 'Período de lactancia', pregnancy: 'Embarazo', disability: 'Discapacidad',
  catastrophic_illness: 'Enfermedad catastrófica (solo sí o no, sin diagnóstico)',
};

export interface EditableCondicion { declared_none: boolean; older_adult: boolean; lactation: boolean; pregnancy: boolean; disability: boolean;
  disability_pct: string; catastrophic_illness: boolean; other_declared: boolean }
export const condicionVacia = (): EditableCondicion => ({ declared_none: false, older_adult: false, lactation: false, pregnancy: false,
  disability: false, disability_pct: '', catastrophic_illness: false, other_declared: false });
export const editableDe = (f: FilaCondicion | null): EditableCondicion => f ? {
  declared_none: f.declared_none, older_adult: f.older_adult, lactation: f.lactation, pregnancy: f.pregnancy, disability: f.disability,
  disability_pct: f.disability_pct === null ? '' : String(f.disability_pct).replace('.', ','), catastrophic_illness: f.catastrophic_illness,
  other_declared: !!f.other_declared,
} : condicionVacia();
/** Texto del origen: trasladado de la ficha anterior y todavía sin revisar por el administrador. */
export const textoOrigen = (f: FilaCondicion) => f.origin === 'traslado_ficha_anterior' && !f.registered_by
  ? 'Trasladado desde la ficha anterior del trabajador al aplicar la actualización; revísalo y guárdalo.' : null;

/** Edad cumplida a la fecha (null sin fecha de nacimiento). */
export function edad(nacimiento: string | null | undefined, hoy: Date): number | null {
  if (!nacimiento) return null;
  const [a, m, d] = nacimiento.slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return null;
  let e = hoy.getFullYear() - a;
  if (hoy.getMonth() + 1 < m || (hoy.getMonth() + 1 === m && hoy.getDate() < d)) e--;
  return e;
}
export const esAdultoMayorPorEdad = (nacimiento: string | null | undefined, hoy: Date) => (edad(nacimiento, hoy) ?? 0) >= EDAD_ADULTO_MAYOR;

export const tieneCondicion = (c: Pick<EditableCondicion, Condicion> & { other_declared?: boolean }) => CONDICIONES.some(k => c[k]) || !!c.other_declared;

/** Validación (las mismas reglas que la base). Devuelve el error o null. */
export function validarCondicion(c: EditableCondicion, adultoPorEdad: boolean): string | null {
  if (c.declared_none && (tieneCondicion(c) || adultoPorEdad))
    return adultoPorEdad ? `Por su fecha de nacimiento, el trabajador tiene ${EDAD_ADULTO_MAYOR} años o más: no se puede declarar sin condición.`
      : 'Elige "Declarado sin condición" o una condición, no ambas.';
  if (!c.declared_none && !tieneCondicion(c) && !adultoPorEdad) return 'Marca al menos una condición o "Declarado sin condición".';
  if (c.disability_pct.trim()) {
    if (!c.disability) return 'El porcentaje solo va con discapacidad.';
    const n = Number(c.disability_pct.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0 || n > 100) return 'El porcentaje de discapacidad va de 1 a 100.';
  }
  return null;
}
export function filaDesdeEditable(c: EditableCondicion) {
  return {
    declared_none: c.declared_none, older_adult: c.older_adult, lactation: c.lactation, pregnancy: c.pregnancy, disability: c.disability,
    disability_pct: c.disability && c.disability_pct.trim() ? Number(c.disability_pct.replace(',', '.')) : null, catastrophic_illness: c.catastrophic_illness,
    other_declared: c.other_declared,
  };
}

/** Textos de pantalla: por qué se pide y quién lo ve. */
export const TEXTO_POR_QUE = 'StorSafe pide esta información para identificar a los trabajadores de grupos de atención prioritaria en las evaluaciones de riesgo de su puesto y planificar medidas específicas para ellos.';
export const TEXTO_QUIEN_VE = 'Es un dato sensible de salud: solo el administrador de la empresa lo ve y lo cambia. Los informes por puesto solo muestran cuántos trabajadores tienen una condición considerada, sin nombres ni tipo de condición. No se guarda ningún diagnóstico.';
export const TEXTO_SOLO_ADMIN = 'Solo el administrador de la empresa puede ver y registrar la condición de atención prioritaria.';

// ── Datos ──
export async function leerCondicion(companyId: string, workerId: string): Promise<{ fila: FilaCondicion | null; disponible: boolean }> {
  try {
    const { data, error } = await supabase.from('worker_priority_conditions').select('*').eq('company_id', companyId).eq('worker_id', workerId).maybeSingle();
    if (error) return { fila: null, disponible: false };
    return { fila: (data as FilaCondicion | null) ?? null, disponible: true };
  } catch {
    return { fila: null, disponible: false };   // sin la tabla (antes de aplicar la migración) o sin conexión
  }
}
export async function guardarCondicion(companyId: string, workerId: string, existente: FilaCondicion | null, c: EditableCondicion): Promise<FilaCondicion> {
  const fila = filaDesdeEditable(c);
  const q = existente
    ? supabase.from('worker_priority_conditions').update(fila).eq('id', existente.id).select('*')
    : supabase.from('worker_priority_conditions').insert({ ...fila, company_id: companyId, worker_id: workerId }).select('*');
  const { data, error } = await q;
  if (error || !data?.length) {
    if (error?.code === '42501' || /row-level security|permission denied/i.test(error?.message ?? '') || (!error && !data?.length)) throw new Error(TEXTO_SOLO_ADMIN);
    throw new Error('No se pudo guardar la condición de atención prioritaria. Intenta de nuevo.');
  }
  return (data as FilaCondicion[])[0];
}
/** Lista para el administrador (en pantalla, nunca en un PDF): qué trabajadores tienen condición declarada. */
export async function leerCondicionesEmpresa(companyId: string): Promise<FilaCondicion[] | null> {
  const { data, error } = await supabase.from('worker_priority_conditions').select('*').eq('company_id', companyId);
  return error ? null : (data as FilaCondicion[]);
}
